import { useMemo } from 'react';

import classNames from 'classnames';

import type { PortableTextComponents } from '@portabletext/react';

import ExpanderList from '@helsenorge/designsystem-react/components/ExpanderList';
import Loader from '@helsenorge/designsystem-react/components/Loader';
import Spacer from '@helsenorge/designsystem-react/components/Spacer';
import Title from '@helsenorge/designsystem-react/components/Title';
import designsystemtypography from '@helsenorge/designsystem-react/scss/typography.module.scss';

import RessursBlock from '@helsenorge/core-cms/behandlingsvalg/ressursblock';
import PortableText from '@helsenorge/core-cms/richtext';
import { globalComponents } from '@helsenorge/core-cms/richtext/portable-text-utils';
import UserFeedback from '@helsenorge/core-cms/userfeedback';
import NotFound from '@helsenorge/core-framework/page-not-found';
import { getSidetittelId } from '@helsenorge/framework-utils/hn-page';

import BehandlingsBeskrivelseBlock from './behandlingsbeskrivelseblock';
import VideoBlockPortableText from '../shared/blocks/VideoBlock/VideoBlockPortableText';
import VideoMatriseBlockPortableText from '../shared/blocks/VideoMatriseBlock/VideoMatriseBlockPortableText';
import { useSamvalgPageContent } from '../shared/useSamvalgPageContent';
import { videoLanguageMapper } from '../shared/videoLanguageMapper';

import behandlingsvalgStyles from './styles.module.scss';

const createVideoBlockComponent = (subtitleLanguageCode: string): PortableTextComponents => ({
  types: {
    ...globalComponents.types,

    VideoBlock: props => <VideoBlockPortableText {...props} value={{ ...props.value, subtitleLanguageCode }} />,
    VideoMatriseBlock: props => <VideoMatriseBlockPortableText {...props} value={{ ...props.value, subtitleLanguageCode }} />,
  },
});

const Behandlingsvalg: React.FunctionComponent = () => {
  const { behandlingsvalgPage, isBehandlingsvalgError: isError, isBehandlingsvalgPageLoading: isLoading } = useSamvalgPageContent();

  const subtitleLanguageCode = useMemo(
    () => behandlingsvalgPage?.languageVersions && videoLanguageMapper(behandlingsvalgPage.languageVersions),
    [behandlingsvalgPage]
  );

  const videoBlockComponent = useMemo(
    () => subtitleLanguageCode && createVideoBlockComponent(subtitleLanguageCode),
    [subtitleLanguageCode]
  );

  if (isLoading) {
    return <Loader center />;
  }

  if (isError || !behandlingsvalgPage) {
    return <NotFound />;
  }

  const introWrapperClasses = classNames('row', behandlingsvalgStyles['introduksjon-wrapper']);
  const behandlingsvalgWrapperClasses = classNames(behandlingsvalgStyles['behandlingsvalg-wrapper']);
  const mainColumnClass = 'col-md-10 offset-md-1 col-lg-8 offset-lg-2';

  return (
    <>
      <title>{behandlingsvalgPage.metaData.browserTitle}</title>
      <meta name="description" content={behandlingsvalgPage.metaData.metaDescription} />
      <meta name="keywords" content={behandlingsvalgPage.metaData.keywords} />
      <div className={'container'}>
        <div className={introWrapperClasses}>
          <div className={mainColumnClass}>
            <Title margin={2} htmlMarkup={'h1'} appearance={'title1'} id={getSidetittelId()}>
              {behandlingsvalgPage.title}
            </Title>
            <p className={classNames(designsystemtypography.preamble, behandlingsvalgStyles.introduksjon)}>
              {behandlingsvalgPage.introduction}
            </p>
          </div>
        </div>
      </div>
      {behandlingsvalgPage.contentArea && behandlingsvalgPage.contentArea.length > 0 && (
        <div className={behandlingsvalgWrapperClasses}>
          <div className={'container'}>
            <div className="row">
              <div className={mainColumnClass}>
                {behandlingsvalgPage.titleMainContentArea && (
                  <>
                    <Title htmlMarkup={'h3'} appearance={'title3'}>
                      {behandlingsvalgPage.titleMainContentArea}
                    </Title>
                    <Spacer size={'l'}></Spacer>
                  </>
                )}
                <ExpanderList color={'blueberry'} renderChildrenWhenClosed>
                  {behandlingsvalgPage.contentArea
                    .filter(mainContentBlock => mainContentBlock.title && mainContentBlock.type === 'BehandlingsvalgBeskrivelseBlock')
                    .map(mainContentBlock => {
                      const blockTitle = mainContentBlock.title ? mainContentBlock.title : '';
                      return (
                        <ExpanderList.Expander key={blockTitle} title={blockTitle} titleHtmlMarkup="h2">
                          <BehandlingsBeskrivelseBlock
                            introduction={mainContentBlock.introduction ? mainContentBlock.introduction : []}
                            mainContent={mainContentBlock.mainContent ? mainContentBlock.mainContent : []}
                            ending={mainContentBlock.avslutning ? mainContentBlock.avslutning : []}
                          />
                        </ExpanderList.Expander>
                      );
                    })}
                </ExpanderList>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className={'container'}>
        {behandlingsvalgPage.otherContent &&
          behandlingsvalgPage.otherContent.length > 0 &&
          behandlingsvalgPage.otherContent.map((content, index) => {
            if (content._type === 'VideoBlock') {
              return (
                <div className="row" key={index}>
                  <div className={mainColumnClass}>
                    <Spacer size={'xl'} />
                    <PortableText value={content} components={videoBlockComponent} />
                  </div>
                </div>
              );
            } else if (content._type === 'VideoMatriseBlock') {
              return (
                <div className="row" key={index}>
                  <div className={'col-md-12  col-lg-12'}>
                    <Spacer size={'xl'} />
                    <PortableText value={content} components={videoBlockComponent} />
                  </div>
                </div>
              );
            } else if (content.type === 'ResourceBlock') {
              return (
                <div className="row" key={index}>
                  <div className={mainColumnClass}>
                    <Spacer size={'xl'} />
                    <RessursBlock resource={content} />
                  </div>
                </div>
              );
            } else if (content.type === 'ImageBlock') {
              return (
                <div className="row" key={index}>
                  <div className={'col-12'}>
                    <Spacer size={'xl'} />
                    <PortableText value={content} />
                  </div>
                </div>
              );
            }
          })}
      </div>
      {!behandlingsvalgPage.hideUserFeedbackBlock && <UserFeedback {...behandlingsvalgPage.userFeedback} />}
    </>
  );
};

export default Behandlingsvalg;
